import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Loader2, ShieldCheck, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login, status } = useAuth();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (status === "authed") {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(identifier, password);
      toast.success(`Hoş geldiniz, ${user.full_name}`);
    } catch (err) {
      setError(formatApiError(err, "Giriş yapılamadı."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#F4F6F8]">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#171A1F] text-white relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FF353A]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#168DEB]/10 blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img
              src="/gungorpen_logo_nobg.png"
              alt="Güngör Pen Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>

        <div className="relative z-10 max-w-md space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[#168DEB] text-xs font-medium uppercase tracking-wider">
            <Building2 size={14} /> Operasyon Platformu
          </div>
          <h1 className="text-3xl font-bold tracking-tight leading-tight text-white">
            İş, sipariş ve maliyet süreçleri için tek merkez.
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            PVC, Alüminyum, Cam Balkon ve Pergola projelerinizi ölçüden montaja kadar tek panelde takip edin.
          </p>
          <div className="pt-2 flex items-center gap-6 text-xs text-slate-400">
            <div className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#168DEB]" /> Güvenli Altyapı</div>
            <div className="flex items-center gap-1.5"><Zap size={16} className="text-[#FF353A]" /> Canlı Takip</div>
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500">
          © {new Date().getFullYear()} Güngör Pen · Kurumsal Operasyon Yönetimi
        </div>
      </div>

      {/* Right form container */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6 rounded-lg border border-[#DCE2E8] bg-white p-8 shadow-sm"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <img
                src="/gungorpen_logo_nobg.png"
                alt="Güngör Pen Logo"
                className="h-8 w-auto object-contain"
              />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#168DEB]">GP Yönetim</div>
            <h2 className="text-2xl font-bold text-[#171A1F]">Giriş Yap</h2>
            <p className="text-sm text-slate-500">Devam etmek için kullanıcı bilgilerinizi girin.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="identifier" className="text-slate-700 text-sm font-medium">Kullanıcı Adı veya E-posta</Label>
              <Input
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="ör. admin"
                autoComplete="username"
                required
                className="border-[#DCE2E8] focus-visible:ring-[#FF353A]"
                data-testid="login-identifier-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-700 text-sm font-medium">Parola</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="border-[#DCE2E8] focus-visible:ring-[#FF353A]"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-[#FF353A] hover:bg-[#E02B30] text-white font-medium h-10 shadow-sm"
            disabled={submitting}
            data-testid="login-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Giriş yapılıyor
              </>
            ) : (
              "Giriş Yap"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
